import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCmsPages, getCmsPage, upsertCmsPage, deleteCmsPage } from '../../api';
import {
  PageHeader, Table, EmptyState, TableSkeleton, StatusBadge,
} from '../../components/ui';
import Modal from '../../components/ui/Modal';
import { formatDateTime } from '../../utils/format';
import { FileText, Pencil, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

const SLUGS = [
  'about', 'contact', 'terms', 'privacy', 'refund-policy', 'services', 'ceo-profile', 'tarot-by-vrinda',
] as const;

type FormValues = {
  titleEn: string;
  titleHi?: string;
  bodyEn: string;
  bodyHi?: string;
  status: 'draft' | 'published';
};

export default function CmsPagesPage() {
  const qc = useQueryClient();
  const [editSlug, setEditSlug] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-cms'],
    queryFn: () => getCmsPages({ limit: 50 }).then((r) => r.data.data.data || r.data.data || []),
  });

  const { data: pageDetail } = useQuery({
    queryKey: ['admin-cms-one', editSlug],
    queryFn: () => getCmsPage(editSlug!).then((r) => r.data.data.page),
    enabled: !!editSlug,
  });

  const { register, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: { titleEn: '', titleHi: '', bodyEn: '', bodyHi: '', status: 'draft' },
  });

  useEffect(() => {
    if (!pageDetail) return;
    const title = pageDetail.title || {};
    const body = pageDetail.body || {};
    reset({
      titleEn: title.en || title.get?.('en') || Object.values(title)[0] || pageDetail.title?.en || '',
      titleHi: title.hi || '',
      bodyEn: body.en || Object.values(body)[0] || '',
      bodyHi: body.hi || '',
      status: pageDetail.status || 'draft',
    });
  }, [pageDetail, reset]);

  const save = useMutation({
    mutationFn: (v: FormValues) =>
      upsertCmsPage(editSlug!, {
        title: { en: v.titleEn, hi: v.titleHi || '' },
        body: { en: v.bodyEn, hi: v.bodyHi || '' },
        status: v.status,
      }),
    onSuccess: () => {
      toast.success('Page saved');
      qc.invalidateQueries({ queryKey: ['admin-cms'] });
      setEditSlug(null);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Save failed'),
  });

  const remove = useMutation({
    mutationFn: (slug: string) => deleteCmsPage(slug),
    onSuccess: () => {
      toast.success('Deleted');
      qc.invalidateQueries({ queryKey: ['admin-cms'] });
    },
  });

  const existing = Array.isArray(data) ? data : [];
  const bySlug = Object.fromEntries(existing.map((p: any) => [p.slug, p]));

  return (
    <div className="p-6">
      <PageHeader title="CMS Pages" subtitle="About, Contact, Terms, Privacy, Refund, Services" />

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-5"><TableSkeleton rows={8} cols={4} /></div>
        ) : (
          <Table>
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="table-th">Slug</th>
                <th className="table-th">Title</th>
                <th className="table-th">Status</th>
                <th className="table-th">Updated</th>
                <th className="table-th"></th>
              </tr>
            </thead>
            <tbody>
              {SLUGS.filter((s) => s !== 'ceo-profile' && s !== 'tarot-by-vrinda').map((slug) => {
                const p = bySlug[slug];
                return (
                  <tr key={slug} className="table-row">
                    <td className="table-td font-mono text-xs">{slug}</td>
                    <td className="table-td text-sm">{p?.title?.en || p?.title || '—'}</td>
                    <td className="table-td">{p ? <StatusBadge status={p.status} /> : <span className="text-xs text-gray-400">missing</span>}</td>
                    <td className="table-td text-xs">{p ? formatDateTime(p.updatedAt) : '—'}</td>
                    <td className="table-td">
                      <div className="flex gap-1 justify-end">
                        <button className="btn-secondary btn-sm" onClick={() => setEditSlug(slug)}><Pencil size={12} /></button>
                        {p && (
                          <button className="btn-secondary btn-sm text-red-600" onClick={() => remove.mutate(slug)}><Trash2 size={12} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
        {!isLoading && existing.length === 0 && (
          <EmptyState message="No CMS pages yet — edit a slug to create" icon={<FileText size={32} />} />
        )}
      </div>

      <Modal open={!!editSlug} onClose={() => setEditSlug(null)} title={`Edit — ${editSlug}`}>
        <form onSubmit={handleSubmit((v) => save.mutate(v))} className="space-y-3">
          <div>
            <label className="block text-xs font-medium mb-1">Title (EN)</label>
            <input className="input" {...register('titleEn')} />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Title (HI)</label>
            <input className="input" {...register('titleHi')} />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Body (EN)</label>
            <textarea className="input font-mono text-xs" rows={8} {...register('bodyEn')} />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Body (HI)</label>
            <textarea className="input font-mono text-xs" rows={4} {...register('bodyHi')} />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Status</label>
            <select className="input" {...register('status')}>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={() => setEditSlug(null)}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={save.isPending}>Save</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
